import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { AverageScoreTeamMode } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAlert } from '@sorare/core/src/atoms/icons/system/iconAlert.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MultipleMatchupItem } from '#src/components/game/MultipleMatchupItem/index.tsx';
import { PlayerGameStatsPrediction } from '#src/components/game/PlayerGameStatsPrediction/index.tsx';
import { SingleMatchupItem } from '#src/components/game/SingleMatchupItem/index.tsx';

import { CalendarTooltip } from './CalendarTooltip/index.tsx';
import type {
  AnyPlayerUpcomingGames_anyGameStat,
  AnyPlayerUpcomingGames_anyPlayer,
} from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: AnyPlayerUpcomingGames_anyPlayer;
  scopedAnyGameStats?: readonly (AnyPlayerUpcomingGames_anyGameStat | null)[];
  teamMode?: AverageScoreTeamMode | null;
};

const Root = styled(Horizontal)`
  background-color: var(--current-row-color);
  padding: 0 var(--unit);
  display: none;
  @media ${laptopAndAbove} {
    display: flex;
    background-color: var(--current-row-color);
    padding: 0 var(--unit);
  }
`;
const RelativeWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: relative;
  isolation: isolate;
`;
const EmptyWrapper = styled(Horizontal).attrs({ center: true })`
  height: var(--triple-unit);
  max-width: 120px;
  width: 100%;
`;

const Matchup = styled(Horizontal).attrs({ gap: 0 })`
  height: var(--triple-unit);
  min-width: 120px;
  border-radius: var(--half-unit);
  background: var(--c-nd-50);
  overflow: hidden;
`;

const Absolute = styled(Horizontal).attrs({ gap: 0.5 })`
  position: absolute;
  top: calc(100% + var(--half-unit));
  left: 0;
  right: 0;
`;

const Divider = styled.div`
  height: 2px;
  width: 2px;
  background-color: var(--c-nd-150);
  border-radius: 50%;
  flex-shrink: 0;
`;

export const AnyPlayerUpcomingGames = ({
  anyPlayer,
  scopedAnyGameStats,
  teamMode,
}: Props) => {
  const anyGameStats = scopedAnyGameStats ?? anyPlayer.anyGameStats;
  const playerTeamSlug =
    teamMode === 'NATIONAL_TEAM'
      ? anyPlayer.activeNationalTeam?.slug
      : anyPlayer.activeClub?.slug;

  const hasMultipleGames = (anyGameStats?.length || 0) > 1;
  const hasAGame = anyGameStats?.length === 1;
  const nextGameStat = anyGameStats?.[0];
  const nextGame = nextGameStat?.anyGame;

  const fixtureName = nextGame?.so5Fixture?.shortDisplayName;

  const anyGamesForFixture = anyGameStats?.map(a => a?.anyGame).filter(Boolean);

  return (
    <Root gap={0.5} center>
      {hasMultipleGames || hasAGame ? (
        <RelativeWrapper>
          <Matchup>
            {!!hasAGame && !!nextGame && (
              <SingleMatchupItem
                anyGame={nextGame}
                playerTeamSlug={playerTeamSlug}
              />
            )}
            {!!hasMultipleGames &&
              anyGamesForFixture?.map(anyGame => (
                <MultipleMatchupItem
                  key={anyGame.id}
                  anyGame={anyGame}
                  playerSlug={anyPlayer.slug}
                  playerTeamSlug={playerTeamSlug}
                />
              ))}
          </Matchup>
          {!!fixtureName && (
            <Absolute center>
              <LabelXS color="var(--c-nd-600)">{fixtureName}</LabelXS>
              {!!hasAGame && isType(nextGameStat, 'PlayerGameStats') && (
                <>
                  {!!nextGameStat?.footballPlayingStatusOdds && (
                    <>
                      <Divider />
                      <LabelXS>
                        <PlayerGameStatsPrediction
                          variant="text"
                          playingStatusOdds={
                            nextGameStat.footballPlayingStatusOdds
                          }
                        />
                      </LabelXS>
                    </>
                  )}
                </>
              )}
            </Absolute>
          )}
        </RelativeWrapper>
      ) : (
        <EmptyWrapper>
          <FontAwesomeIcon
            icon={iconAlert}
            color="var(--c-yellow-400)"
            width={10}
          />
          <LabelXS className="text-center">
            <FormattedMessage defaultMessage="No game next GW" />
          </LabelXS>
        </EmptyWrapper>
      )}
      <CalendarTooltip
        anyPlayer={anyPlayer}
        hasAGame={!!anyPlayer.nextGame}
        teamMode={teamMode}
      />
    </Root>
  );
};

export const anyPlayerUpcomingGamesAnyGameStatFragment = gql`
  fragment AnyPlayerUpcomingGames_anyGameStat on AnyPlayerGameStatsInterface {
    id
    anyGame {
      id
      so5Fixture {
        slug
        shortDisplayName
      }
      ...SingleMatchupItem_anyGame
      ...MultipleMatchupItem_anyGame
    }
    ... on PlayerGameStats {
      id
      footballPlayingStatusOdds {
        reliability
        ...PlayerGameStatsPrediction_playingStatusOdds
      }
    }
  }
  ${SingleMatchupItem.fragments.anyGame}
  ${MultipleMatchupItem.fragments.anyGame}
  ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
` as TypedDocumentNode<AnyPlayerUpcomingGames_anyGameStat>;

AnyPlayerUpcomingGames.fragments = {
  anyPlayer: gql`
    fragment AnyPlayerUpcomingGames_anyPlayer on AnyPlayerInterface {
      slug
      activeClub {
        slug
      }
      activeNationalTeam {
        slug
      }
      nextGame {
        id
      }
      anyGameStats(so5FixtureSlug: $so5FixtureSlug, last: 10)
        @skip(if: $withoutSo5Fixture) {
        id
        ...AnyPlayerUpcomingGames_anyGameStat
      }
      ...CalendarTooltip_anyPlayer
    }
    ${anyPlayerUpcomingGamesAnyGameStatFragment}
    ${CalendarTooltip.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyPlayerUpcomingGames_anyPlayer>,
};
